/**
 * The edited-grid cache in `sessionStorage` holds only the rows that carry an
 * edit. A large input (NTV) has too many rows and period columns to store whole
 * (`QuotaExceededError`); the unedited rows are rebuilt from the loaded data.
 */
interface IGridRow {
  Input_RowID: string;
  updatedCells: string[];
}

export const getModifiedRows = <T extends IGridRow>(rows: T[]): T[] =>
  rows.filter((row) => row.updatedCells.length > 0);

/** `baseRows` with each stored edited row put in place of the row with the same `Input_RowID`. */
export const mergeModifiedRows = <T extends IGridRow>(
  baseRows: T[],
  stored: string | null | undefined
): T[] => {
  if (!stored) {
    return baseRows;
  }
  const modifiedById = new Map(
    (JSON.parse(stored) as T[]).map((row) => [row.Input_RowID, row])
  );
  return baseRows.map((row) => modifiedById.get(row.Input_RowID) ?? row);
};

// Fallback for edits too big for sessionStorage (a bulk upload can edit thousands of NTV rows).
// Without it the key is dropped and the grid silently shows the old, unedited data.
const memoryFallback = new Map<string, string>();

/** The stored edited rows for `key`: sessionStorage first, then the in-memory fallback. */
export const readModifiedRows = (key: string): string | null =>
  sessionStorage.getItem(key) ?? memoryFallback.get(key) ?? null;

/** Forget the edited rows for `key` everywhere they may be held. */
export const clearModifiedRows = (key: string): void => {
  memoryFallback.delete(key);
  sessionStorage.removeItem(key);
};

/** Forget every input's edited rows. */
export const clearAllModifiedRows = (): void => {
  memoryFallback.clear();
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith('gridState-inputName-'))
    .forEach((key) => sessionStorage.removeItem(key));
};

/**
 * Store the edited rows under `key`. If the browser refuses (quota), the key
 * is removed from sessionStorage so a later restore never loads an older, stale
 * copy, and the rows are held in memory instead (lost on reload).
 */
export const persistModifiedRows = (key: string, rows: IGridRow[]): void => {
  const serialized = JSON.stringify(getModifiedRows(rows));
  try {
    sessionStorage.setItem(key, serialized);
    memoryFallback.delete(key);
  } catch (error) {
    sessionStorage.removeItem(key);
    memoryFallback.set(key, serialized);
    // eslint-disable-next-line no-console
    console.warn(
      `Edited rows too large for sessionStorage (${(error as Error).name}); kept in memory until reload.`
    );
  }
};
